import { useEffect, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence, useMotionValue, useSpring, useScroll } from "framer-motion";
import { Clock, Users, X, ArrowUpRight, Play, Instagram, Youtube, Sparkles, Plus, Heart, Search, Printer, Share2, ShoppingBasket, Flame, HeartHandshake, MapPin, ArrowUp } from "lucide-react";
import { Toaster, toast } from "sonner";
import { LOGO_URL, recipes, products, charities, careers, CAREERS_EMAIL, t } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tr = (key, lang) => (t[key] ? t[key][lang] : key);
const num = (i) => String(i + 1).padStart(2, "0");
const VIDEO_THUMB = "https://img.youtube.com/vi/EvfUfHhb4XE/hqdefault.jpg";

const scaleQty = (str, f) => {
  if (f === 1) return str;
  const m = str.match(/^(\d+\/\d+|\d+(?:\.\d+)?)/);
  if (!m) return str;
  const tok = m[1];
  let val;
  if (tok.includes("/")) { const [a, b] = tok.split("/"); val = parseFloat(a) / parseFloat(b); }
  else val = parseFloat(tok);
  const scaled = val * f;
  const out = Number.isInteger(scaled) ? String(scaled) : String(Math.round(scaled * 100) / 100);
  return out + str.slice(tok.length);
};

const NAV_IDS = ["recipes", "shop", "give", "careers", "about"];
const NAV_KEYS = { recipes: "nav_recipes", shop: "shop_kicker", give: "nav_give", careers: "nav_careers", about: "nav_about" };

function Header({ lang, setLang }) {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const [active, setActive] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV_IDS.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <header className={`sticky top-0 z-50 bg-[#FBFAF7]/80 backdrop-blur-md border-b transition-[border-color,box-shadow,background-color] duration-300 ${scrolled ? "border-[#E2E0D7] shadow-[0_6px_24px_rgba(26,26,23,0.05)] bg-[#FBFAF7]/92" : "border-[#ECEAE3]"}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button data-testid="logo-home-button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="group flex items-center gap-2.5">
          <img src={LOGO_URL} alt="Ăn Khỏe Project" className="h-10 w-10 rounded-full object-cover ring-2 ring-[#6F7F63] ring-offset-2 ring-offset-[#FBFAF7] transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="font-display text-lg font-semibold text-[#1A1A17] tracking-tight">Ăn Khỏe Project</span>
        </button>
        <div className="flex items-center gap-0.5 sm:gap-1">
          {NAV_IDS.map((id) => (
            <button
              key={id}
              data-testid={`nav-${id}-button`}
              onClick={() => go(id)}
              className={`relative hidden sm:block px-3 py-2 text-sm font-medium transition-colors ${active === id ? "text-[#1A1A17]" : "text-[#6B6B63] hover:text-[#1A1A17]"}`}
            >
              {tr(NAV_KEYS[id], lang)}
              {active === id && (
                <motion.span layoutId="nav-underline" transition={{ type: "spring", stiffness: 420, damping: 34 }} className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full bg-[#6F7F63]" />
              )}
            </button>
          ))}
          <div data-testid="language-toggle" className="ml-1.5 flex items-center text-xs font-medium tracking-wide border border-[#1A1A17]/15 rounded-full overflow-hidden">
            {["en", "vi"].map((code) => (
              <button key={code} data-testid={`lang-${code}-button`} onClick={() => setLang(code)} className={`px-3 py-1.5 transition-colors ${lang === code ? "bg-[#1A1A17] text-[#FBFAF7]" : "text-[#6B6B63] hover:text-[#1A1A17]"}`}>{code.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, restDelta: 0.001 });
  return (
    <motion.div
      data-testid="scroll-progress"
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 z-[55] h-[3px] origin-left bg-gradient-to-r from-[#6F7F63] via-[#C66B4E] to-[#E6B23E]"
    />
  );
}

function CursorGlow() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 380, damping: 30, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 380, damping: 30, mass: 0.4 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const move = (e) => {
      x.set(e.clientX - 14);
      y.set(e.clientY - 14);
      const el = e.target;
      setActive(!!(el && el.closest("a, button, [role=button], input, [data-testid]")));
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);
  return (
    <motion.div
      aria-hidden="true"
      style={{ x: sx, y: sy, scale: active ? 2.1 : 1 }}
      transition={{ scale: { type: "spring", stiffness: 300, damping: 20 } }}
      className="hidden md:block fixed top-0 left-0 w-7 h-7 rounded-full bg-[#FBFAF7] mix-blend-difference pointer-events-none z-[60]"
    />
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          data-testid="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          initial={{ opacity: 0, scale: 0.6, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 16 }}
          whileHover={{ y: -3 }}
          whileTap={{ scale: 0.92 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-[55] w-12 h-12 rounded-full bg-[#1A1A17] text-[#FBFAF7] shadow-[0_10px_30px_rgba(26,26,23,0.25)] flex items-center justify-center hover:bg-[#2d2d27]"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

function Hero({ lang, onExplore }) {
  const [spin, setSpin] = useState(0);
  return (
    <section className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-6">
      <Sparkles className="hidden sm:block absolute left-[44%] top-24 w-5 h-5 text-[#F0C44C] ak-twinkle pointer-events-none" />
      <Sparkles className="hidden md:block absolute right-[8%] bottom-24 w-4 h-4 text-[#6F7F63] ak-twinkle pointer-events-none" style={{ animationDelay: "1.2s" }} />
      <Sparkles className="hidden lg:block absolute left-[2%] top-1/2 w-4 h-4 text-[#C66B4E] ak-twinkle pointer-events-none" style={{ animationDelay: "0.6s" }} />
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em] text-[#9A9A90] border-b border-[#ECEAE3] pb-3">
        <span>Issue 01</span>
        <span>Home Kitchen</span>
        <span className="hidden sm:block invisible">Eat well, live well</span>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.8, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.8, delay: 0.15, type: "spring", stiffness: 120 }} className="hidden md:block absolute right-2 lg:right-6 top-32 lg:top-36 z-10">
        <div className="relative ak-float">
          <span className="absolute inset-0 -m-8 rounded-full bg-[#6F7F63]/15 blur-2xl" />
          <motion.button data-testid="mascot-button" onClick={() => { setSpin((s) => s + 1); onExplore(); }} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 12 }} className="relative block cursor-pointer" aria-label="Ăn Khỏe Project mascot">
            <motion.img src={LOGO_URL} alt="Ăn Khỏe Project mascot" animate={{ rotate: spin * 360 }} transition={{ type: "spring", stiffness: 120, damping: 13 }} className="w-44 lg:w-56 h-44 lg:h-56 rounded-full object-cover ring-1 ring-[#1A1A17]/10 shadow-[0_20px_50px_rgba(26,26,23,0.18)]" />
          </motion.button>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="pt-6 sm:pt-10">
        <h1 className="font-display font-semibold tracking-[-0.03em] text-[#1A1A17] leading-[0.95] text-[15vw] sm:text-[11vw] lg:text-[8.5rem]">
          {lang === "en" ? "Eat well," : "Ăn ngon,"}
          <span className="block italic font-normal text-[#6F7F63]">{lang === "en" ? "live well." : "sống khỏe."}</span>
        </h1>
        <div className="mt-6 sm:mt-10 grid md:grid-cols-12 gap-6 items-end">
          <p className="md:col-span-5 text-base sm:text-lg text-[#4a4a42] leading-relaxed">{tr("hero_sub", lang)}</p>
          <div className="md:col-span-4 md:col-start-9 md:text-right">
            <button data-testid="hero-explore-button" onClick={onExplore} className="inline-flex items-center gap-2 bg-[#1A1A17] text-[#FBFAF7] font-medium px-6 py-3 rounded-full hover:gap-3 hover:bg-[#2d2d27] transition-all active:scale-95">{tr("hero_cta", lang)} <ArrowUpRight className="w-4 h-4" /></button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function FavBtn({ id, favorites, onToggleFav, light }) {
  const fav = favorites.includes(id);
  return (
    <button data-testid={`recipe-fav-${id}`} onClick={(e) => { e.stopPropagation(); onToggleFav(id); }} aria-label="favorite"
      className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-all active:scale-90 ${light ? "bg-white/85 hover:bg-white" : "bg-[#FBFAF7]/85 hover:bg-white"}`}>
      <Heart className={`w-4 h-4 transition-all ${fav ? "fill-[#C66B4E] text-[#C66B4E]" : "text-[#1A1A17]"}`} />
    </button>
  );
}

function RecipeIndex({ lang, onOpen, onPlayVideo, favorites, onToggleFav }) {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");

  const catObjs = [];
  const seen = {};
  recipes.forEach((r) => { if (!seen[r.category.en]) { seen[r.category.en] = 1; catObjs.push(r.category); } });

  const filtered = recipes.filter((r) => {
    if (cat === "loved") return favorites.includes(r.id);
    if (cat !== "all" && r.category.en !== cat) return false;
    if (query) return (r.title.en + r.title.vi + r.blurb.en + r.blurb.vi).toLowerCase().includes(query.toLowerCase());
    return true;
  });
  const featured = filtered[0];
  const rest = filtered.slice(1);
  const showVideo = cat === "all" && !query;
  const featuredIdx = featured ? recipes.indexOf(featured) : 0;

  return (
    <section id="recipes" className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <div className="flex items-baseline justify-between border-b-2 border-[#1A1A17] pb-3">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17]">{tr("recipes_title", lang)}</h2>
        <span className="text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]">{recipes.length} dishes</span>
      </div>

      {/* search + filters */}
      <div className="mt-5 flex items-center gap-3 justify-between">
        <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0">
          {[{ key: "all", label: tr("filter_all", lang) }, ...catObjs.map((c) => ({ key: c.en, label: c[lang] })), { key: "loved", label: tr("filter_loved", lang) }].map((c) => (
            <button key={c.key} data-testid={`filter-${c.key}`} onClick={() => setCat(c.key)} className={`shrink-0 text-xs font-medium px-3.5 py-1.5 rounded-full border transition-all ${cat === c.key ? "bg-[#1A1A17] text-[#FBFAF7] border-[#1A1A17]" : "border-[#E2E0D7] text-[#6B6B63] hover:border-[#1A1A17]"}`}>
              {c.key === "loved" ? <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{c.label}</span> : c.label}
            </button>
          ))}
        </div>
        <div className="relative shrink-0 w-36 sm:w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" />
          <input data-testid="recipe-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tr("search_ph", lang)} className="w-full bg-white border border-[#E2E0D7] rounded-full pl-9 pr-3 py-2 text-sm outline-none focus:border-[#6F7F63] transition-colors" />
        </div>
      </div>

      {filtered.length === 0 && (
        <p data-testid="no-results" className="py-16 text-center text-[#8a8a80]">{tr("no_results", lang)}</p>
      )}

      {/* featured */}
      {featured && (
        <motion.div data-testid={`recipe-card-${featured.id}`} onClick={() => onOpen(featured)} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="group cursor-pointer mt-8 grid md:grid-cols-2 gap-6 lg:gap-10 items-center">
          <div className="relative overflow-hidden rounded-3xl aspect-[4/3]">
            <img src={featured.image} alt={featured.title[lang]} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[600ms]" />
            <span className="absolute top-4 left-5 font-display text-5xl sm:text-6xl text-white/85 leading-none drop-shadow">{num(featuredIdx)}</span>
            <FavBtn id={featured.id} favorites={favorites} onToggleFav={onToggleFav} light />
            <span className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-[#FBFAF7]/90 backdrop-blur px-3 py-1 rounded-full text-[#1A1A17]">{featured.category[lang]}</span>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#6F7F63]">Featured</span>
            <h3 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A17] tracking-tight leading-[1.02]">{featured.title[lang]}</h3>
            <p className="mt-3 text-[#6B6B63] leading-relaxed max-w-md">{featured.blurb[lang]}</p>
            <div className="mt-5 flex items-center gap-5 text-sm text-[#8a8a80]">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {featured.minutes} {tr("minutes", lang)}</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {featured.serves}</span>
              <span className="flex items-center gap-1.5"><Flame className="w-4 h-4 text-[#C66B4E]" /> {featured.nutrition.kcal} {tr("kcal", lang)}</span>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 font-medium text-[#1A1A17] border-b-2 border-[#1A1A17] pb-0.5 group-hover:gap-3 transition-all">{tr("view_recipe", lang)} <ArrowUpRight className="w-4 h-4" /></span>
          </div>
        </motion.div>
      )}

      {/* grid of the rest + video */}
      {(rest.length > 0 || showVideo) && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {rest.map((r, i) => (
            <motion.div key={r.id} data-testid={`recipe-card-${r.id}`} onClick={() => onOpen(r)} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: i * 0.06 }} className="group cursor-pointer relative overflow-hidden rounded-2xl aspect-[4/5]">
              <img src={r.image} alt={r.title[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <span className="absolute top-3 left-4 font-display text-3xl text-white/85 leading-none drop-shadow">{num(recipes.indexOf(r))}</span>
              <FavBtn id={r.id} favorites={favorites} onToggleFav={onToggleFav} light />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <span className="text-[9px] uppercase tracking-[0.22em] text-white/80">{r.category[lang]}</span>
                <h3 className="mt-1 font-display text-lg sm:text-2xl font-medium text-white leading-tight">{r.title[lang]}</h3>
                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-out">
                  <div className="overflow-hidden">
                    <div className="flex items-center gap-3 pt-2 text-[11px] text-white/85">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {r.minutes} {tr("minutes", lang)}</span>
                      <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> {r.nutrition.kcal} {tr("kcal", lang)}</span>
                      <ArrowUpRight className="w-4 h-4 ml-auto" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {showVideo && (
            <motion.button data-testid="video-card" onClick={onPlayVideo} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: rest.length * 0.06 }} className="group text-left relative overflow-hidden rounded-2xl aspect-[4/5] bg-black">
              <img src={VIDEO_THUMB} alt="" className="w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-[600ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-[#FBFAF7]/90 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform"><Play className="w-6 h-6 text-[#1A1A17] fill-[#1A1A17] ml-0.5" /></span>
              </span>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <span className="text-[9px] uppercase tracking-[0.22em] text-white/80">{tr("video_kicker", lang)}</span>
                <h3 className="mt-1 font-display text-lg sm:text-2xl font-medium text-white leading-tight">{tr("video_title", lang)}</h3>
              </div>
            </motion.button>
          )}
        </div>
      )}
    </section>
  );
}

function RecipeModal({ recipe, lang, onClose, favorites, onToggleFav }) {
  const [mult, setMult] = useState(1);
  useEffect(() => { setMult(1); }, [recipe]);
  if (!recipe) return null;
  const fav = favorites.includes(recipe.id);

  const printRecipe = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const ings = recipe.ingredients[lang].map((x) => `<li>${scaleQty(x, mult)}</li>`).join("");
    const stps = recipe.steps[lang].map((x) => `<li>${x}</li>`).join("");
    w.document.write(`<html><head><title>${recipe.title[lang]}</title><style>body{font-family:Georgia,serif;max-width:640px;margin:40px auto;color:#1A1A17;padding:0 24px}h1{font-size:32px}h2{font-size:14px;text-transform:uppercase;letter-spacing:2px;color:#6F7F63;margin-top:28px}li{margin:6px 0;line-height:1.5}.meta{color:#888}</style></head><body><h1>${recipe.title[lang]}</h1><p class="meta">${recipe.minutes} ${tr("minutes", lang)} · ${tr("servings_label", lang)} ${recipe.serves * mult}</p><p>${recipe.blurb[lang]}</p><h2>${tr("ingredients", lang)}</h2><ul>${ings}</ul><h2>${tr("steps", lang)}</h2><ol>${stps}</ol><p class="meta">Ăn Khỏe Project</p></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const share = async () => {
    const url = `${window.location.origin}/#recipes`;
    try { await navigator.clipboard.writeText(`${recipe.title[lang]} — Ăn Khỏe Project ${url}`); } catch (e) {}
    toast(tr("share_done", lang));
  };

  const grocery = () => {
    const lines = recipe.ingredients[lang].map((x) => `- ${scaleQty(x, mult)}`).join("\n");
    const blob = new Blob([`${recipe.title[lang]}\n\n${lines}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${recipe.id}-grocery.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const nut = recipe.nutrition;
  return (
    <AnimatePresence>
      <motion.div data-testid="recipe-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[60] bg-[#1A1A17]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6">
        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} transition={{ type: "spring", damping: 28, stiffness: 260 }} onClick={(e) => e.stopPropagation()} className="bg-[#FBFAF7] w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl">
          <div className="relative">
            <img src={recipe.image} alt={recipe.title[lang]} className="w-full h-56 sm:h-64 object-cover sm:rounded-t-3xl" />
            <button data-testid="recipe-modal-close" onClick={onClose} className="absolute top-4 right-4 bg-[#FBFAF7] hover:bg-white rounded-full p-2 transition-colors"><X className="w-5 h-5 text-[#1A1A17]" /></button>
            <button data-testid="modal-fav" onClick={() => onToggleFav(recipe.id)} className="absolute top-4 right-16 bg-[#FBFAF7] hover:bg-white rounded-full p-2 transition-colors"><Heart className={`w-5 h-5 ${fav ? "fill-[#C66B4E] text-[#C66B4E]" : "text-[#1A1A17]"}`} /></button>
          </div>

          <div className="p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17] tracking-tight">{recipe.title[lang]}</h2>
            <p className="mt-2 text-[#6B6B63] leading-relaxed">{recipe.blurb[lang]}</p>

            <div className="mt-4 flex flex-wrap gap-2 text-sm text-[#9A9A90]">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {recipe.minutes} {tr("minutes", lang)}</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {recipe.serves * mult} {tr("serves", lang)}</span>
              <span className="flex items-center gap-1.5"><Flame className="w-4 h-4 text-[#C66B4E]" /> {nut.kcal} {tr("kcal", lang)}</span>
            </div>

            {/* nutrition per serving */}
            <div className="mt-5 grid grid-cols-3 gap-2">
              {[{ l: tr("macro_protein", lang), v: nut.protein, c: "#6F7F63" }, { l: tr("macro_carbs", lang), v: nut.carbs, c: "#E6B23E" }, { l: tr("macro_fat", lang), v: nut.fat, c: "#C66B4E" }].map((m) => (
                <div key={m.l} className="text-center rounded-xl bg-white border border-[#ECEAE3] py-2.5">
                  <div className="w-2 h-2 rounded-full mx-auto" style={{ backgroundColor: m.c }} />
                  <div className="mt-1 font-display text-lg font-semibold text-[#1A1A17]">{m.v}g</div>
                  <div className="text-[11px] text-[#8a8a80]">{m.l}</div>
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-[#9A9A90] text-center">{tr("nutrition_title", lang)}</p>

            {/* servings scaler */}
            <div className="mt-5 flex items-center gap-3">
              <span className="text-sm text-[#6B6B63] font-medium">{tr("servings_label", lang)}</span>
              <div className="flex items-center gap-1.5">
                {[1, 2, 4].map((n) => (
                  <button key={n} data-testid={`servings-x${n}`} onClick={() => setMult(n)} className={`w-9 h-9 rounded-full text-sm font-medium border transition-all ${mult === n ? "bg-[#1A1A17] text-[#FBFAF7] border-[#1A1A17]" : "border-[#E2E0D7] text-[#6B6B63] hover:border-[#1A1A17]"}`}>×{n}</button>
                ))}
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-5 gap-8">
              <div className="sm:col-span-2">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A9A90]">{tr("ingredients", lang)}</h3>
                <ul className="mt-3 space-y-2">
                  {recipe.ingredients[lang].map((ing, i) => (
                    <li key={i} className="text-[#1A1A17] text-sm border-b border-[#ECEAE3] pb-2">{scaleQty(ing, mult)}</li>
                  ))}
                </ul>
              </div>
              <div className="sm:col-span-3">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A9A90]">{tr("steps", lang)}</h3>
                <ol className="mt-3 space-y-4">
                  {recipe.steps[lang].map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-display text-[#6F7F63] font-medium tabular-nums">{num(i)}</span>
                      <span className="text-[#1A1A17] text-sm leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* actions */}
            <div className="mt-8 flex flex-wrap gap-2 pt-5 border-t border-[#ECEAE3]">
              <button data-testid="modal-print" onClick={printRecipe} className="inline-flex items-center gap-2 text-sm font-medium border border-[#E2E0D7] rounded-full px-4 py-2 hover:border-[#1A1A17] transition-colors"><Printer className="w-4 h-4" /> {tr("print_btn", lang)}</button>
              <button data-testid="modal-share" onClick={share} className="inline-flex items-center gap-2 text-sm font-medium border border-[#E2E0D7] rounded-full px-4 py-2 hover:border-[#1A1A17] transition-colors"><Share2 className="w-4 h-4" /> {tr("share_btn", lang)}</button>
              <button data-testid="modal-grocery" onClick={grocery} className="inline-flex items-center gap-2 text-sm font-medium border border-[#E2E0D7] rounded-full px-4 py-2 hover:border-[#1A1A17] transition-colors"><ShoppingBasket className="w-4 h-4" /> {tr("grocery_btn", lang)}</button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ProductCard({ p, lang, index, onBuy, loadingId }) {
  const [img, setImg] = useState(p.image);
  const [active, setActive] = useState(p.variants ? p.variants[0].hex : null);
  const loading = loadingId === p.id;
  return (
    <motion.div data-testid={`product-card-${p.id}`} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: index * 0.07 }} className="group">
      <div className="relative overflow-hidden rounded-xl bg-[#F1EFE8] aspect-square">
        <img src={img} alt={p.name[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-2.5 left-2.5 text-[9px] uppercase tracking-[0.18em] bg-[#FBFAF7]/90 backdrop-blur px-2 py-0.5 rounded-full text-[#1A1A17]">{p.tag[lang]}</span>
      </div>
      <div className="mt-2 flex items-start justify-between gap-1.5">
        <h3 className="font-display text-xs sm:text-sm font-medium text-[#1A1A17] leading-tight min-w-0">{p.name[lang]}</h3>
        <span className="font-display text-xs sm:text-sm text-[#1A1A17] shrink-0">{p.price}</span>
      </div>
      <p className="hidden sm:block mt-0.5 text-xs text-[#8a8a80] line-clamp-1">{p.desc[lang]}</p>
      {p.variants && (
        <div className="mt-1.5 flex items-center gap-1">
          {p.variants.map((v) => (
            <button key={v.hex} data-testid={`product-color-${p.id}-${v.hex.replace("#", "")}`} onClick={() => { setImg(v.image); setActive(v.hex); }} aria-label={`color ${v.hex}`} className={`w-4 h-4 rounded-full border transition-all ${active === v.hex ? "ring-2 ring-offset-1 ring-[#1A1A17] border-transparent" : "border-[#E2E0D7] hover:scale-110"}`} style={{ backgroundColor: v.hex }} />
          ))}
        </div>
      )}
      <button data-testid={`product-buy-${p.id}`} disabled={loading} onClick={() => onBuy(p.id, active)} className="mt-2 w-full inline-flex items-center justify-center gap-1 bg-[#1A1A17] text-[#FBFAF7] text-[11px] sm:text-xs font-medium py-2 rounded-full hover:bg-[#2d2d27] active:scale-[0.98] transition-all disabled:opacity-60">
        <Plus className="w-3 h-3" /> {loading ? tr("shop_loading", lang) : tr("shop_buy", lang)}
      </button>
    </motion.div>
  );
}

function Shop({ lang }) {
  const [loadingId, setLoadingId] = useState(null);
  const buy = async (productId, variant) => {
    setLoadingId(productId);
    try {
      const res = await fetch(`${API}/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: productId, origin_url: window.location.origin, variant: variant || null }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else throw new Error("no url");
    } catch (e) {
      toast(tr("shop_error", lang));
      setLoadingId(null);
    }
  };
  return (
    <section id="shop" className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-12 border-t border-[#ECEAE3]">
      <div className="flex items-end justify-between border-b border-[#1A1A17] pb-2.5">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]"><span className="w-1.5 h-1.5 rounded-full bg-[#6F7F63]" />{tr("shop_kicker", lang)}</span>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17] tracking-tight">{tr("shop_title", lang)}</h2>
        </div>
        <p className="hidden sm:block max-w-xs text-sm text-[#8a8a80] text-right">{tr("shop_sub", lang)}</p>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {products.map((p, i) => (<ProductCard key={p.id} p={p} lang={lang} index={i} onBuy={buy} loadingId={loadingId} />))}
      </div>
    </section>
  );
}

function MacroCalc({ lang }) {
  const [form, setForm] = useState({ weight: "", height: "", age: "", sex: "female", activity: "1.375", goal: "maintain" });
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const calc = (e) => {
    e.preventDefault();
    const w = parseFloat(form.weight), h = parseFloat(form.height), a = parseFloat(form.age);
    if (!w || !h || !a) return;
    let bmr = 10 * w + 6.25 * h - 5 * a + (form.sex === "male" ? 5 : -161);
    let cals = bmr * parseFloat(form.activity);
    if (form.goal === "lose") cals -= cals * 0.15;
    if (form.goal === "gain") cals += cals * 0.15;
    cals = Math.round(cals);
    setResult({ cals, protein: Math.round((cals * 0.3) / 4), carbs: Math.round((cals * 0.4) / 4), fat: Math.round((cals * 0.3) / 9) });
  };
  const field = "w-full bg-[#FBFAF7] border border-[#E2E0D7] rounded-xl px-3 py-2.5 text-sm text-[#1A1A17] outline-none focus:border-[#6F7F63] transition-colors";
  return (
    <section id="macros" className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16 border-t border-[#ECEAE3]">
      <div className="text-center mb-6">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]"><span className="w-1.5 h-1.5 rounded-full bg-[#6F7F63]" />{tr("macro_kicker", lang)}</span>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17] tracking-tight">{tr("macro_title", lang)}</h2>
        <p className="mt-2 text-sm text-[#8a8a80]">{tr("macro_sub", lang)}</p>
      </div>
      <form onSubmit={calc} className="bg-white rounded-2xl border border-[#ECEAE3] shadow-[0_12px_40px_rgba(26,26,23,0.05)] p-5 sm:p-7">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <label className="block"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_weight", lang)}</span><input data-testid="macro-weight" type="number" min="1" value={form.weight} onChange={(e) => set("weight", e.target.value)} className={`mt-1 ${field}`} placeholder="60" /></label>
          <label className="block"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_height", lang)}</span><input data-testid="macro-height" type="number" min="1" value={form.height} onChange={(e) => set("height", e.target.value)} className={`mt-1 ${field}`} placeholder="165" /></label>
          <label className="block"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_age", lang)}</span><input data-testid="macro-age" type="number" min="1" value={form.age} onChange={(e) => set("age", e.target.value)} className={`mt-1 ${field}`} placeholder="28" /></label>
          <label className="block"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_sex", lang)}</span><select data-testid="macro-sex" value={form.sex} onChange={(e) => set("sex", e.target.value)} className={`mt-1 ${field}`}><option value="female">{tr("macro_female", lang)}</option><option value="male">{tr("macro_male", lang)}</option></select></label>
          <label className="block col-span-2 sm:col-span-1"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_goal", lang)}</span><select data-testid="macro-goal" value={form.goal} onChange={(e) => set("goal", e.target.value)} className={`mt-1 ${field}`}><option value="lose">{tr("goal_lose", lang)}</option><option value="maintain">{tr("goal_maintain", lang)}</option><option value="gain">{tr("goal_gain", lang)}</option></select></label>
          <label className="block col-span-2 sm:col-span-3"><span className="text-xs text-[#6B6B63] font-medium">{tr("macro_activity", lang)}</span><select data-testid="macro-activity" value={form.activity} onChange={(e) => set("activity", e.target.value)} className={`mt-1 ${field}`}><option value="1.2">{tr("act_sedentary", lang)}</option><option value="1.375">{tr("act_light", lang)}</option><option value="1.55">{tr("act_moderate", lang)}</option><option value="1.725">{tr("act_active", lang)}</option></select></label>
        </div>
        <button data-testid="macro-calc-button" type="submit" className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1A1A17] text-[#FBFAF7] text-sm font-medium px-7 py-3 rounded-full hover:bg-[#2d2d27] active:scale-[0.98] transition-all">{tr("macro_calc", lang)}</button>
        {result && (
          <motion.div data-testid="macro-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 pt-6 border-t border-[#ECEAE3]">
            <div className="flex items-end gap-2 justify-center"><span className="font-display text-4xl sm:text-5xl font-semibold text-[#1A1A17]">{result.cals.toLocaleString()}</span><span className="text-sm text-[#8a8a80] pb-1.5">{tr("macro_calories", lang)}</span></div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[{ label: tr("macro_protein", lang), val: result.protein, c: "#6F7F63" }, { label: tr("macro_carbs", lang), val: result.carbs, c: "#E6B23E" }, { label: tr("macro_fat", lang), val: result.fat, c: "#C66B4E" }].map((m) => (
                <div key={m.label} className="text-center rounded-xl bg-[#FBFAF7] border border-[#ECEAE3] py-3"><div className="w-2 h-2 rounded-full mx-auto" style={{ backgroundColor: m.c }} /><div className="mt-1.5 font-display text-2xl font-semibold text-[#1A1A17]">{m.val}g</div><div className="text-xs text-[#8a8a80]">{m.label}</div></div>
              ))}
            </div>
          </motion.div>
        )}
      </form>
      <p className="mt-4 text-center text-xs text-[#9A9A90] italic max-w-md mx-auto">{tr("macro_disclaimer", lang)}</p>
    </section>
  );
}

function Newsletter({ lang }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/newsletter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch (err) {
      toast(tr("news_error", lang));
    }
  };
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <div className="bg-[#1A1A17] rounded-3xl px-6 sm:px-12 py-10 sm:py-14 text-center">
        <h2 className="font-display text-2xl sm:text-4xl font-semibold text-[#FBFAF7] tracking-tight">{tr("news_title", lang)}</h2>
        <p className="mt-3 text-[#C9C7BD] max-w-md mx-auto">{tr("news_sub", lang)}</p>
        {done ? (
          <p data-testid="newsletter-success" className="mt-6 inline-block bg-[#6F7F63] text-white font-medium px-6 py-3 rounded-full">{tr("news_done", lang)}</p>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2.5 max-w-md mx-auto">
            <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tr("news_ph", lang)} className="flex-1 bg-[#FBFAF7] rounded-full px-5 py-3 text-sm text-[#1A1A17] outline-none" />
            <button data-testid="newsletter-submit" type="submit" className="bg-[#FBFAF7] text-[#1A1A17] text-sm font-medium px-6 py-3 rounded-full hover:bg-white active:scale-95 transition-all whitespace-nowrap">{tr("news_btn", lang)}</button>
          </form>
        )}
      </div>
    </section>
  );
}

function GiveBack({ lang }) {
  return (
    <section id="give" className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 border-t border-[#ECEAE3]">
      <div className="flex items-end justify-between border-b border-[#1A1A17] pb-2.5">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]"><span className="w-1.5 h-1.5 rounded-full bg-[#C66B4E]" />{tr("give_kicker", lang)}</span>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17] tracking-tight">{tr("give_title", lang)}</h2>
        </div>
        <p className="hidden sm:block max-w-xs text-sm text-[#8a8a80] text-right">{tr("give_sub", lang)}</p>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#ECEAE3] border border-[#ECEAE3] rounded-2xl overflow-hidden">
        {charities.map((c, i) => (
          <motion.div
            key={c.id}
            data-testid={`charity-card-${c.id}`}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: i * 0.07 }}
            className="group relative flex flex-col bg-[#FBFAF7] p-6 hover:bg-white transition-colors"
          >
            <span className="absolute top-0 left-0 h-0.5 w-0 bg-current transition-all duration-500 group-hover:w-full" style={{ color: c.accent }} />
            <div className="flex items-center justify-between">
              <span className="font-display text-sm text-[#C9C7BD]">{num(i)}</span>
              <span className="text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full" style={{ backgroundColor: `${c.accent}14`, color: c.accent }}>{c.focus[lang]}</span>
            </div>
            <h3 className="mt-5 font-display text-lg font-medium text-[#1A1A17] leading-snug">{c.name}</h3>
            <p className="mt-2 flex-1 text-sm text-[#8a8a80] leading-relaxed">{c.blurb[lang]}</p>
            <a
              data-testid={`charity-donate-${c.id}`}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-1.5 border border-[#1A1A17]/15 text-[#1A1A17] text-xs font-medium py-2.5 rounded-full hover:border-[#1A1A17] hover:bg-[#1A1A17] hover:text-[#FBFAF7] transition-all"
            >
              {tr("give_btn", lang)} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        ))}
      </div>
      <p data-testid="give-disclaimer" className="mt-5 flex items-start gap-2.5 text-xs text-[#9A9A90] leading-relaxed max-w-2xl">
        <HeartHandshake className="w-4 h-4 mt-px shrink-0 text-[#C0BEB4]" />
        <span>{tr("give_disclaimer", lang)}</span>
      </p>
    </section>
  );
}

function Careers({ lang }) {
  const subject = encodeURIComponent("An Khoe Project application");
  return (
    <section id="careers" className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 border-t border-[#ECEAE3]">
      <div className="flex items-end justify-between border-b border-[#1A1A17] pb-2.5">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]"><span className="w-1.5 h-1.5 rounded-full bg-[#6F7F63]" />{tr("career_kicker", lang)}</span>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-[#1A1A17] tracking-tight">{tr("career_title", lang)}</h2>
        </div>
        <p className="hidden sm:block max-w-xs text-sm text-[#8a8a80] text-right">{tr("career_sub", lang)}</p>
      </div>
      <div className="mt-2">
        {careers.map((role, i) => (
          <motion.div
            key={role.id}
            data-testid={`career-card-${role.id}`}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="group grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] sm:items-center gap-4 sm:gap-8 py-7 border-b border-[#ECEAE3]"
          >
            <span className="hidden sm:block font-display text-2xl text-[#C9C7BD] group-hover:text-[#1A1A17] transition-colors">{num(i)}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="font-display text-xl font-medium text-[#1A1A17] leading-tight">{role.title[lang]}</h3>
              </div>
              <p className="mt-1.5 text-sm text-[#8a8a80] leading-relaxed max-w-xl">{role.blurb[lang]}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#6B6B63] border border-[#E2E0D7] rounded-full px-3 py-1"><Flame className="w-3.5 h-3.5" style={{ color: role.accent }} />{role.type[lang]}</span>
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#6B6B63] border border-[#E2E0D7] rounded-full px-3 py-1"><MapPin className="w-3.5 h-3.5" style={{ color: role.accent }} />{role.location[lang]}</span>
              </div>
            </div>
            <a
              data-testid={`career-apply-${role.id}`}
              href={`mailto:${CAREERS_EMAIL}?subject=${subject} ${encodeURIComponent(role.title.en)}`}
              className="justify-self-start sm:justify-self-end shrink-0 inline-flex items-center justify-center gap-1.5 border border-[#1A1A17] text-[#1A1A17] text-xs font-medium px-6 py-2.5 rounded-full hover:bg-[#1A1A17] hover:text-[#FBFAF7] active:scale-[0.98] transition-all"
            >
              {tr("career_apply", lang)} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl bg-[#F1EFE8] px-6 py-5">
        <p className="text-sm text-[#6B6B63]">{tr("career_empty_note", lang)}</p>
        <a
          data-testid="career-open-application"
          href={`mailto:${CAREERS_EMAIL}?subject=${subject}`}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-[#1A1A17] underline underline-offset-4 decoration-[#C66B4E] hover:decoration-[#1A1A17]"
        >
          {tr("career_open_apps", lang)} <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
}

function VideoModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="video-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[60] bg-[#1A1A17]/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ type: "spring", damping: 26, stiffness: 240 }} onClick={(e) => e.stopPropagation()} className="relative">
            <button data-testid="video-modal-close" onClick={onClose} className="absolute -top-12 right-0 sm:-right-12 sm:top-0 bg-white rounded-full p-2.5 hover:scale-110 transition-transform"><X className="w-5 h-5 text-[#1A1A17]" /></button>
            <div className="w-[300px] sm:w-[340px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-2xl">
              <iframe data-testid="kitchen-video" className="w-full h-full block" src="https://www.youtube.com/embed/EvfUfHhb4XE?autoplay=1&rel=0&modestbranding=1" title="Ăn Khỏe Project kitchen" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function App() {
  const [lang, setLang] = useState("en");
  const [active, setActive] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ak_favs") || "[]"); } catch (e) { return []; }
  });

  useEffect(() => { document.body.style.overflow = active || videoOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [active, videoOpen]);

  const toggleFav = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("ak_favs", JSON.stringify(next));
      toast(prev.includes(id) ? tr("fav_removed", lang) : tr("fav_added", lang));
      return next;
    });
  };

  // handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const purchase = params.get("purchase");
    if (purchase === "cancel") {
      toast(tr("pay_cancel", lang));
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) return;
      attempts += 1;
      try {
        const res = await fetch(`${API}/checkout/status/${sid}`);
        const data = await res.json();
        if (data.payment_status === "paid") { toast(tr("pay_success", lang)); window.history.replaceState({}, "", window.location.pathname); return; }
        if (data.status === "expired") { toast(tr("pay_cancel", lang)); window.history.replaceState({}, "", window.location.pathname); return; }
        setTimeout(poll, 1800);
      } catch (e) { /* ignore */ }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const scrollToRecipes = () => document.getElementById("recipes")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="App">
      <ScrollProgress />
      <CursorGlow />
      <div className="ak-grain" aria-hidden="true" />
      <div className="hidden lg:flex fixed right-3 top-1/2 -translate-y-1/2 z-30 items-center text-[10px] uppercase tracking-[0.4em] text-[#B5B5AB] [writing-mode:vertical-rl] pointer-events-none">Ăn Khỏe Project · Est. 2024</div>

      <Header lang={lang} setLang={setLang} />
      <Hero lang={lang} onExplore={scrollToRecipes} />
      <RecipeIndex lang={lang} onOpen={setActive} onPlayVideo={() => setVideoOpen(true)} favorites={favorites} onToggleFav={toggleFav} />
      <Shop lang={lang} />
      <MacroCalc lang={lang} />
      <GiveBack lang={lang} />

      <section id="about" className="bg-[#6F7F63] py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/70"><span className="w-1.5 h-1.5 rounded-full bg-white/80" />{tr("about_title", lang)}</span>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ type: "spring", stiffness: 90, damping: 16 }} className="relative bg-white rounded-[1.75rem] border border-[#ECEAE3] shadow-[0_20px_50px_rgba(26,26,23,0.06)] px-6 sm:px-12 pt-24 pb-12">
            <div className="absolute left-1/2 -top-12 -translate-x-1/2">
              <div className="ak-float relative">
                <span className="absolute inset-0 -m-3 rounded-full bg-[#6F7F63]/12 blur-xl" />
                <img src={LOGO_URL} alt="Ăn Khỏe mascot" className="relative w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-md" />
              </div>
            </div>
            <p className="text-center text-base sm:text-lg text-[#3a3a33] leading-relaxed max-w-xl mx-auto">{tr("about_p1", lang)}</p>
            <p className="mt-8 text-center font-display italic text-base text-[#6F7F63]">{tr("about_sign", lang)}</p>
          </motion.div>
        </div>
      </section>

      <Careers lang={lang} />

      <Newsletter lang={lang} />

      <footer className="border-t border-[#ECEAE3]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
          <div className="grid gap-10 sm:grid-cols-12">
            <div className="sm:col-span-6">
              <div className="flex items-center gap-2.5">
                <img src={LOGO_URL} alt="Ăn Khỏe" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#1A1A17]/10" />
                <span className="font-display text-lg font-semibold text-[#1A1A17] tracking-tight">Ăn Khỏe Project</span>
              </div>
              <p className="mt-4 max-w-xs text-sm text-[#8a8a80] leading-relaxed">{tr("footer_tag", lang)}</p>
            </div>
            <div className="sm:col-span-3">
              <h4 className="text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]">{tr("footer_explore", lang)}</h4>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><button data-testid="footer-recipes-link" onClick={() => document.getElementById("recipes")?.scrollIntoView({ behavior: "smooth" })} className="text-[#4a4a42] hover:text-[#1A1A17] transition-colors">{tr("nav_recipes", lang)}</button></li>
                <li><button data-testid="footer-give-link" onClick={() => document.getElementById("give")?.scrollIntoView({ behavior: "smooth" })} className="text-[#4a4a42] hover:text-[#1A1A17] transition-colors">{tr("nav_give", lang)}</button></li>
                <li><button data-testid="footer-careers-link" onClick={() => document.getElementById("careers")?.scrollIntoView({ behavior: "smooth" })} className="text-[#4a4a42] hover:text-[#1A1A17] transition-colors">{tr("nav_careers", lang)}</button></li>
                <li><button data-testid="footer-about-link" onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} className="text-[#4a4a42] hover:text-[#1A1A17] transition-colors">{tr("nav_about", lang)}</button></li>
                <li><button data-testid="footer-top-link" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-[#4a4a42] hover:text-[#1A1A17] transition-colors">{tr("footer_top", lang)}</button></li>
              </ul>
            </div>
            <div className="sm:col-span-3">
              <h4 className="text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]">{tr("footer_follow", lang)}</h4>
              <div className="mt-4 flex gap-3">
                <a data-testid="footer-instagram" href="#" onClick={(e) => e.preventDefault()} aria-label="Instagram" className="w-10 h-10 rounded-full border border-[#E2E0D7] flex items-center justify-center text-[#1A1A17] hover:bg-[#1A1A17] hover:text-white transition-all"><Instagram className="w-4 h-4" /></a>
                <a data-testid="footer-youtube" href="https://www.youtube.com/shorts/EvfUfHhb4XE" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 rounded-full border border-[#E2E0D7] flex items-center justify-center text-[#1A1A17] hover:bg-[#1A1A17] hover:text-white transition-all"><Youtube className="w-4 h-4" /></a>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-[#ECEAE3] flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]">© 2024 Ăn Khỏe Project</p>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#9A9A90]">{tr("footer_made", lang)}</p>
          </div>
        </div>
      </footer>

      {active && <RecipeModal recipe={active} lang={lang} onClose={() => setActive(null)} favorites={favorites} onToggleFav={toggleFav} />}
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
      <BackToTop />
      <Toaster position="bottom-center" toastOptions={{ style: { background: "#1A1A17", color: "#FBFAF7", border: "none", borderRadius: "9999px", fontFamily: "Libre Franklin, sans-serif" } }} />
    </div>
  );
}

export default App;
